import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useLearningManagementServiceV1 } from '../api'
import {
  LearningEvent,
  LearningEventWithoutId,
} from '../auto-generated-clients/learning-management-client-v1.generated'
import { v4 as uuid } from 'uuid'

interface UseLearningEventsArgs {
  contentMetadataId?: string
  releaseChannelId: string
  selectedLearningEventId?: string
  setSelectedLearningEvent: (learningEvent: LearningEvent | undefined) => void
  isEditingEventTemplate: boolean
}

const LEARNING_EVENTS_QUERY_KEY = 'learningEvents'

export const useLearningEvents = ({
  contentMetadataId,
  releaseChannelId,
  selectedLearningEventId,
  setSelectedLearningEvent,
  isEditingEventTemplate,
}: UseLearningEventsArgs) => {
  const lmsV1 = useLearningManagementServiceV1()

  const queryClient = useQueryClient()

  const invalidateQueries = () => {
    queryClient.invalidateQueries({
      queryKey: [LEARNING_EVENTS_QUERY_KEY, contentMetadataId, releaseChannelId],
    })
  }

  const fetchLearningEvents = () =>
    lmsV1.getAllLearningEvents(contentMetadataId, 0, 1000)

  const { data: learningEvents, isLoading: isLearningEventsQueryLoading } =
    useQuery({
      queryKey: [LEARNING_EVENTS_QUERY_KEY, contentMetadataId, releaseChannelId],
      queryFn: fetchLearningEvents,
      enabled: !!contentMetadataId,
    })

  const addLearningEvent = () => {
    if (isEditingEventTemplate) {
      return Promise.reject(
        new Error(
          'Please save or cancel the current event template before adding a new learning event.'
        )
      )
    }

    const newLearningEvent = new LearningEventWithoutId({
      contentMetadataId: contentMetadataId ?? '',
      learningEventName: `Event ${uuid().slice(0, 5)}`,
    })

    return lmsV1.createLearningEvent(newLearningEvent)
  }

  const { mutate: addLearningEventMutation } = useMutation({
    mutationFn: addLearningEvent,
    onSuccess: async (addedLearningEvent) => {
      invalidateQueries()
      setSelectedLearningEvent(addedLearningEvent)
    },
    onError: (error) => {
      alert(error.message)
    },
  })

  const deleteLearningEvent = async () => {
    if (isEditingEventTemplate) {
      return Promise.reject(
        new Error(
          'Please save or cancel the current event template before deleting a learning event.'
        )
      )
    }
    const deletedEventIndex = learningEvents?.findIndex(
      (e) => e.learningEventId === selectedLearningEventId
    )
    if (selectedLearningEventId) {
      await lmsV1.deleteLearningEventById(selectedLearningEventId)
    }

    return deletedEventIndex ?? 0
  }

  const { mutate: deleteLearningEventMutation } = useMutation({
    mutationFn: deleteLearningEvent,
    onSuccess: (deletedIndex) => {
      const newSelectedIndex = deletedIndex === 0 ? 1 : deletedIndex - 1
      invalidateQueries()
      setSelectedLearningEvent(learningEvents?.[newSelectedIndex])
    },
    onError: (error) => {
      alert(error.message)
    },
  })

  const updateLearningEvent = async (learningEventName: string) => {
    const updatedLearningEventWithoutId = new LearningEventWithoutId({
      learningEventName,
      contentMetadataId: contentMetadataId ?? '',
    })

    if (selectedLearningEventId) {
      await lmsV1.updateLearningEvent(
        selectedLearningEventId,
        updatedLearningEventWithoutId
      )

      return updatedLearningEventWithoutId
    }
  }

  const { mutate: updateLearningEventMutation } = useMutation({
    mutationFn: updateLearningEvent,
    onSuccess: (updatedLearningEventWithoutId) => {
      invalidateQueries()

      if (updatedLearningEventWithoutId && selectedLearningEventId) {
        const updatedLearningEvent = {
          ...updatedLearningEventWithoutId,
          learningEventId: selectedLearningEventId,
        } as LearningEvent

        setSelectedLearningEvent(updatedLearningEvent)
      }
    },
  })

  return {
    learningEvents,
    isLoading: isLearningEventsQueryLoading,
    addLearningEventMutation,
    deleteLearningEventMutation,
    updateLearningEventMutation,
  }
}
